<script setup lang="ts">
/**
 * 弹窗。遮罩淡入、面板缩放淡入，Esc 关闭，打开时锁滚动。
 * 用 Teleport 挂到 body —— 否则父级的 overflow/transform 会把 fixed 定位关进容器里。
 */
import { onBeforeUnmount, ref, watch } from 'vue'

import { useZIndex } from 'element-plus'

import { cn } from '@/lib/utils'

import { X } from './icons'

// 根是 `<Teleport>` 且内含两个 Transition ⇒ 多根节点，Vue 无处自动落透传属性，
// 调用方写的 `data-test` / `aria-*` 会被静默丢弃。改为手动绑到面板节点上。
defineOptions({ inheritAttrs: false })

const props = withDefaults(
  defineProps<{
    modelValue: boolean
    title?: string
    description?: string
    width?: string
    class?: string
    contentClass?: string
    /**
     * 层级档位。`top` 用于**从另一个对话框里弹出**的确认框。
     *
     * 同档位时后挂载的那个在上（Teleport 按挂载顺序追加到 body 末尾），
     * 而 `ConfirmHost` 是全局单例、挂载得最早 —— 不抬档的话，对话框里点
     * 「删除」弹出的确认框会被那个对话框整片盖住，确认按钮点不到。
     */
    layer?: 'default' | 'top'
    /**
     * 不可误关：点遮罩、按 Esc 都不关，右上角也不给关闭按钮 —— 只能由内容里的
     * 按钮显式关掉（台账 OA-19 ③）。
     *
     * 给**一次性内容**用。真实来源是 API Key 的明文框：那串 key 关掉之后要再跑
     * 一趟回显接口才能拿到，而遮罩正是最容易误点的地方。此前它只能留在裸
     * `el-dialog`（靠 `close-on-click-modal="false"` + `:show-close="false"`），
     * 于是同一个平台有两套弹层。
     *
     * ★ 默认 `false`，行为与从前逐字相同 —— 已有的 40+ 个调用点一个都不用改。
     */
    persistent?: boolean
  }>(),
  {
    title: '',
    description: '',
    width: '32rem',
    class: '',
    contentClass: '',
    layer: 'default',
    persistent: false,
  },
)

/**
 * 层级在**每次打开时**从 Element Plus 的全局计数器取号 —— 谁后开谁在上，
 * 与宿主是不是抽屉无关。静态 z-[1001] 在 el-drawer（2000 起逐个递增）里
 * 必然被整片压住（2026-08-18 线上实测：部署抽屉里的传输弹窗点了看不见）。
 * `layer='top'` 再抬一档，保证从对话框里弹出的确认框恒在其宿主之上。
 */
const { nextZIndex } = useZIndex()
const zBase = ref(2001)
const emit = defineEmits<{ 'update:modelValue': [value: boolean] }>()

function close(): void {
  emit('update:modelValue', false)
}

/**
 * 遮罩点击与 Esc 走这里，右上角那个按钮**不走** —— 它是显式意图。
 *
 * `persistent` 挡的正是「误关」：手滑点到遮罩、随手按 Esc。
 */
function closeIfDismissible(): void {
  if (!props.persistent) close()
}

function onKeydown(e: KeyboardEvent): void {
  if (e.key === 'Escape') closeIfDismissible()
}

watch(
  () => props.modelValue,
  (open) => {
    if (open) zBase.value = nextZIndex() + (props.layer === 'top' ? 100 : 0)
    document.body.style.overflow = open ? 'hidden' : ''
    if (open) document.addEventListener('keydown', onKeydown)
    else document.removeEventListener('keydown', onKeydown)
  },
)

onBeforeUnmount(() => {
  document.body.style.overflow = ''
  document.removeEventListener('keydown', onKeydown)
})
</script>

<template>
  <Teleport to="body">
    <Transition
      enter-active-class="transition-opacity duration-200"
      enter-from-class="opacity-0"
      leave-active-class="transition-opacity duration-150"
      leave-to-class="opacity-0"
    >
      <!-- 轻微背景模糊：把弹窗从页面上托出来，遮罩不用压得更黑。 -->
      <div
        v-if="modelValue"
        class="fixed inset-0 bg-foreground/40 backdrop-blur-[2px]"
        :style="{ zIndex: zBase }"
        @click="closeIfDismissible"
      />
    </Transition>

    <Transition
      enter-active-class="transition duration-200 ease-out"
      enter-from-class="opacity-0 scale-95"
      leave-active-class="transition duration-150 ease-in"
      leave-to-class="opacity-0 scale-95"
    >
      <div
        v-if="modelValue"
        class="fixed inset-0 flex items-center justify-center p-4"
        :style="{ zIndex: zBase + 1 }"
        data-test="dialog"
        role="dialog"
        aria-modal="true"
        @click.self="closeIfDismissible"
      >
        <div
          v-bind="$attrs"
          :class="
            cn(
              'flex max-h-[85vh] w-full flex-col rounded-xl border border-border bg-card shadow-lg',
              props.class,
            )
          "
          :style="{ maxWidth: width }"
        >
          <header
            v-if="title || description"
            class="flex items-start justify-between gap-4 border-b border-border px-5 py-4"
          >
            <div class="min-w-0">
              <h2 v-if="title" class="text-base font-semibold">{{ title }}</h2>
              <p v-if="description" class="mt-1 text-sm text-muted-foreground">{{ description }}</p>
            </div>
            <!-- `persistent` 时不给这个按钮：留着它，「不可误关」就只挡住了一半，
                 而右上角那个 X 恰恰是最顺手的关闭方式。此时只能由内容里的按钮关。 -->
            <button
              v-if="!persistent"
              type="button"
              class="flex size-8 shrink-0 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-[1px] focus-visible:ring-ring/30"
              aria-label="关闭"
              data-test="dialog-close"
              @click="close"
            >
              <X :size="16" aria-hidden="true" />
            </button>
          </header>

          <div :class="cn('min-h-0 flex-1 overflow-y-auto px-5 py-4', props.contentClass)">
            <slot />
          </div>

          <footer
            v-if="$slots.footer"
            class="flex items-center justify-end gap-2 border-t border-border px-5 py-3"
          >
            <slot name="footer" />
          </footer>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>
